import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SCAN_TYPES, SUBJECTS } from './ScanFilters'
import { Sparkles, Link2, Search } from 'lucide-react'
import { api } from '../../lib/api'

interface Assignment {
  id: number
  name: string
  course_id: number
  course_name: string
  due_at: string | null
  points_possible: number | null
  score: number | null
}

interface CategorizationFormProps {
  currentSubject: string | null
  currentScanType: string | null
  currentTitle: string | null
  linkedAssignment?: { id: number; name: string; course_name: string | null } | null
  canLinkAssignment?: boolean
  onSave: (data: { subject?: string; scan_type?: string; title?: string; assignment_id?: number }) => void
  isSaving: boolean
  showXPHint?: boolean
}

export function CategorizationForm({
  currentSubject,
  currentScanType,
  currentTitle,
  linkedAssignment,
  canLinkAssignment = true,
  onSave,
  isSaving,
  showXPHint = true,
}: CategorizationFormProps) {
  const [subject, setSubject] = useState(currentSubject || '')
  const [scanType, setScanType] = useState(currentScanType || '')
  const [title, setTitle] = useState(currentTitle || '')
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null)
  const [showAssignmentSearch, setShowAssignmentSearch] = useState(false)
  const [assignmentSearch, setAssignmentSearch] = useState('')

  // Fetch linkable assignments
  const { data: assignmentsData } = useQuery({
    queryKey: ['linkable-assignments', assignmentSearch],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (assignmentSearch) params.set('search', assignmentSearch)
      const response = await api.get(`/api/student/scans/linkable-assignments?${params}`)
      return response.data as { assignments: Assignment[]; courses: { id: number; name: string }[] }
    },
    enabled: showAssignmentSearch && canLinkAssignment && !linkedAssignment,
  })

  const hasChanges =
    subject !== (currentSubject || '') ||
    scanType !== (currentScanType || '') ||
    title !== (currentTitle || '') ||
    selectedAssignmentId !== null

  // Calculate potential XP
  const potentialXP =
    (!currentSubject && subject ? 5 : 0) +
    (!currentScanType && scanType ? 3 : 0) +
    (!currentTitle && title ? 2 : 0) +
    (!linkedAssignment && selectedAssignmentId ? 5 : 0)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: { subject?: string; scan_type?: string; title?: string; assignment_id?: number } = {}
    if (subject && subject !== currentSubject) data.subject = subject
    if (scanType && scanType !== currentScanType) data.scan_type = scanType
    if (title && title !== currentTitle) data.title = title
    if (selectedAssignmentId) data.assignment_id = selectedAssignmentId
    onSave(data)
  }

  const selectedAssignment = assignmentsData?.assignments.find(a => a.id === selectedAssignmentId)

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* XP hint */}
      {showXPHint && potentialXP > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-quest-accent/10 border border-quest-accent/30 rounded-lg">
          <Sparkles className="w-4 h-4 text-quest-accent" />
          <span className="text-sm text-quest-accent">
            Add missing info to earn up to <strong>+{potentialXP} XP</strong>
          </span>
        </div>
      )}

      {/* Type */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          Type
          {!currentScanType && <span className="text-quest-accent ml-1">(+3 XP)</span>}
        </label>
        <select
          value={scanType}
          onChange={(e) => setScanType(e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
        >
          <option value="">Select type...</option>
          {SCAN_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          Subject
          {!currentSubject && <span className="text-quest-accent ml-1">(+5 XP)</span>}
        </label>
        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
        >
          <option value="">Select subject...</option>
          {SUBJECTS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          Title
          {!currentTitle && <span className="text-quest-accent ml-1">(+2 XP)</span>}
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Chapter 5 Test"
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
        />
      </div>

      {/* Assignment linking */}
      {canLinkAssignment && (
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            <Link2 className="w-3 h-3 inline mr-1" />
            Link to Assignment
            {!linkedAssignment && <span className="text-quest-accent ml-1">(+5 XP)</span>}
          </label>

          {linkedAssignment ? (
            <div className="px-3 py-2 bg-green-900/20 border border-green-700/50 rounded-lg text-sm">
              <p className="text-green-400 font-medium">{linkedAssignment.name}</p>
              <p className="text-gray-500 text-xs">{linkedAssignment.course_name}</p>
            </div>
          ) : selectedAssignment ? (
            <div className="px-3 py-2 bg-quest-primary/20 border border-quest-primary/50 rounded-lg text-sm flex items-center justify-between">
              <div>
                <p className="text-quest-primary font-medium">{selectedAssignment.name}</p>
                <p className="text-gray-500 text-xs">{selectedAssignment.course_name}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAssignmentId(null)}
                className="text-gray-400 hover:text-white"
              >
                &times;
              </button>
            </div>
          ) : showAssignmentSearch ? (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={assignmentSearch}
                  onChange={(e) => setAssignmentSearch(e.target.value)}
                  placeholder="Search assignments..."
                  className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                />
              </div>

              {assignmentsData && assignmentsData.assignments.length > 0 ? (
                <div className="max-h-40 overflow-y-auto space-y-1 bg-surface-card rounded-lg p-2">
                  {assignmentsData.assignments.map((assignment) => (
                    <button
                      key={assignment.id}
                      type="button"
                      onClick={() => {
                        setSelectedAssignmentId(assignment.id)
                        setShowAssignmentSearch(false)
                      }}
                      className="w-full text-left px-3 py-2 rounded hover:bg-surface-hover transition-colors"
                    >
                      <p className="text-sm font-medium truncate">{assignment.name}</p>
                      <p className="text-xs text-gray-500">{assignment.course_name}</p>
                    </button>
                  ))}
                </div>
              ) : assignmentsData ? (
                <p className="text-sm text-gray-500 text-center py-2">No assignments found</p>
              ) : null}

              <button
                type="button"
                onClick={() => setShowAssignmentSearch(false)}
                className="text-sm text-gray-400 hover:text-white"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAssignmentSearch(true)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 border-dashed rounded-lg text-gray-400 text-sm hover:border-quest-primary hover:text-quest-primary transition-colors"
            >
              + Link to assignment
            </button>
          )}
        </div>
      )}

      {/* Save button */}
      <button
        type="submit"
        disabled={!hasChanges || isSaving}
        className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Saving...' : hasChanges ? `Save Changes${potentialXP > 0 ? ` (+${potentialXP} XP)` : ''}` : 'No Changes'}
      </button>
    </form>
  )
}
